import {
  getHalfHourlyIncrements,
  getListOfMonths,
  HalfHourlyIncrement,
  Month,
} from "../../types";
import { HalfHourlyConsumptionTariffs } from "./load-profiles";
import { z, ZodNumber } from "zod";
import {
  HAS_EV_OPTIONS,
  HAS_SOLAR_OPTIONS,
  HEATING_TYPE_OPTIONS,
  HOUSE_TYPE_OPTIONS,
  METER_TYPE_OPTIONS,
  SOLAR_ARRAY_SIZE,
} from "../energy-usage/types";
import { DeepPartial } from "../../utils/type-helpers";

export type MonthlyCostComparison = {
  costWithBattery: number;
  costWithoutBattery: number;
};

export type YearlyCost = {
  months: { [K in Month]: number };
  totalCost: number;
};

export type TariffUsedForLastYear = "market" | "custom";
export type TariffUsedForComparison = "market" | "last-year" | "custom";

export type YearlyCostComparison = {
  months: { [K in Month]: MonthlyCostComparison };
  totalCostWithBatteryAndVat: number;
  totalCostWithoutBatteryAndVat: number;
  totalSavingsWithVat: number;
};

export type PartialYearlyCostComparison = DeepPartial<YearlyCostComparison>;

export const isFullYearlyCost = (
  yearlyCost: DeepPartial<YearlyCost>,
): yearlyCost is YearlyCost => {
  return getListOfMonths().every(
    (month) => yearlyCost.months?.[month] !== undefined,
  );
};

export const isFullYearlyCostComparison = (
  yearlyComparison: PartialYearlyCostComparison,
): yearlyComparison is YearlyCostComparison => {
  return (
    yearlyComparison.totalCostWithBatteryAndVat !== undefined &&
    yearlyComparison.totalCostWithoutBatteryAndVat !== undefined &&
    yearlyComparison.totalSavingsWithVat !== undefined &&
    getListOfMonths().every(
      (month) => yearlyComparison.months?.[month] !== undefined,
    )
  );
};

export type TariffWithStandingAndExport = {
  importTariffName: string;
  importTariff: HalfHourlyConsumptionTariffs;
  standingCharge: number;
  exportRate?: number;
};

export const POSTCODE_REGEX = /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i;

const TARIFF_SCHEMA = z.object({
  standingCharge: z.number(),
  exportRate: z.number().optional(),
  importTariff: z.object({
    type: z.literal("tariff"),
    ...(Object.fromEntries(
      getHalfHourlyIncrements().map((increment) => [increment, z.number()]),
    ) as { [key in HalfHourlyIncrement]: ZodNumber }),
  }),
});

const HOUSEHOLD_INFORMATION_SCHEMA = z.object({
  postcode: z.string().regex(POSTCODE_REGEX).trim().optional(),
  hasEv: z.enum(HAS_EV_OPTIONS).optional(),
  meterType: z.enum(METER_TYPE_OPTIONS).optional(),
  houseType: z.enum(HOUSE_TYPE_OPTIONS).optional(),
  heatingType: z.enum(HEATING_TYPE_OPTIONS).optional(),
});

const SYSTEM_CONFIGURATION_SCHEMA = z.object({
  batteryCapacity: z.number().min(0).max(1000),
});

const SOLAR_CONFIGURATION_SCHEMA = z.object({
  hasSolar: z.enum(HAS_SOLAR_OPTIONS).optional(),
  solarArraySize: z.enum(SOLAR_ARRAY_SIZE).optional(),
});

export const SIMPLE_SAVINGS_SCHEMA = z.object({
  householdInformation: HOUSEHOLD_INFORMATION_SCHEMA.and(
    z.object({
      annualConsumptionKwh: z.coerce.number().min(0).optional(),
    }),
  ).optional(),
  systemConfiguration: SYSTEM_CONFIGURATION_SCHEMA,
  lastYearsTariff: TARIFF_SCHEMA.optional(),
  comparisonTariff: TARIFF_SCHEMA.optional(),
});

export type SimpleSavingsOptions = z.infer<typeof SIMPLE_SAVINGS_SCHEMA>;

const GRID_KWH_SCHEMA = z.object({
  import: z.number(),
  export: z.number(),
});

export type GridKwh = z.infer<typeof GRID_KWH_SCHEMA>;

export const SOLAR_SAVINGS_SCHEMA = z.object({
  householdInformation: HOUSEHOLD_INFORMATION_SCHEMA.and(
    z.object({
      gridKwh: GRID_KWH_SCHEMA.optional(),
    }),
  ).optional(),
  systemConfiguration: SYSTEM_CONFIGURATION_SCHEMA,
  solarConfiguration: SOLAR_CONFIGURATION_SCHEMA.optional(),
  lastYearsTariff: TARIFF_SCHEMA.optional(),
  comparisonTariff: TARIFF_SCHEMA.optional(),
});

export type SolarSavingsOptions = z.infer<typeof SOLAR_SAVINGS_SCHEMA>;

export type SavingsResponse = YearlyCostComparison & {
  meta: {
    isSolar: boolean;
    outcode: string;
    incode: string;
    region: string;
    tariffUsedForLastYear: TariffUsedForLastYear;
    tariffUsedForComparison: TariffUsedForComparison;

    annualConsumptionKwh?: number;
    gridKwh?: GridKwh;
  };
};
